<script>
import ApplicantLayout from "../../layouts/applicant";
import AppIcon from "@/components/shared/AppIcon.vue";
import StatusChip from "@/components/shared/StatusChip.vue";
import axios from "axios";
import { formatDate } from "@/helpers/dateFormatter";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const isBlank = (v) =>
  v === null || v === undefined || v === "" || v === "undefined" || v === "null";

// "male" -> "Male", "common_low" -> "Common Law"; values that are already
// formatted (capitals, digits, symbols) are shown as saved.
const pretty = (v) => {
  if (isBlank(v)) return "";
  const text = String(v).trim();
  if (text === "common_low") return "Common Law";
  if (/^[a-z_ ]+$/.test(text)) {
    return text
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return text;
};

const yesNo = (v) => {
  if (isBlank(v)) return "";
  const text = String(v).toLowerCase();
  if (text === "true") return "Yes";
  if (text === "false") return "No";
  return pretty(v);
};

// 2026-10-27 -> "Oct 27, 2026" (parsed by hand so the day never shifts with the time zone)
const day = (v) => {
  if (isBlank(v)) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[3]}, ${m[1]}` : String(v);
};

const phone = (code, number) => {
  if (isBlank(number)) return "";
  return isBlank(code) ? String(number) : `${code} ${number}`;
};

const money = (v) => {
  if (isBlank(v)) return "";
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default {
  name: "ApplicantViewApplication",
  components: { ApplicantLayout, AppIcon, StatusChip },
  data() {
    return {
      loading: true,
      loadError: false,
      app: null,
      docs: null,
    };
  },
  computed: {
    appId() {
      return this.$route.params.id;
    },
    isMinor() {
      return !!this.app && this.app.is_adult === false;
    },
    applicantName() {
      const a = (this.app && this.app.section_a1) || {};
      return [a.applicant_title, a.first_name, a.middle_name, a.sur_name]
        .filter((p) => !isBlank(p))
        .join(" ");
    },
    summary() {
      const app = this.app || {};
      return [
        { label: "Application No.", value: app.application_no || `ECU-${String(this.appId).padStart(6, "0")}` },
        { label: "Applicant", value: this.applicantName || "N/A" },
        { label: "Membership Type", value: this.isMinor ? "Minor" : "Adult" },
        { label: "Residency", value: pretty(app.residency_type) || "N/A" },
        { label: "Started On", value: app.created_on ? formatDate(app.created_on) : "N/A" },
      ];
    },
    // Every section as { title, groups: [{ title?, rows: [[label, value], ...] }] }.
    // Sections the applicant has not filled in (or that do not apply) are left out.
    sections() {
      const app = this.app;
      if (!app) return [];
      const list = [];
      const add = (title, source, groups) => {
        if (source) list.push({ title, groups });
      };

      const a1 = app.section_a1;
      if (a1) {
        add("Personal Information", a1, [
          {
            rows: [
              ["Branch", a1.branch],
              ["Title", a1.applicant_title],
              ["First Name", a1.first_name],
              ["Middle Name", a1.middle_name],
              ["Surname", a1.sur_name],
              ["Date of Birth", day(a1.dob)],
              ["Gender", pretty(a1.gender)],
              ["Marital Status", pretty(a1.marital)],
              ["Dependents", a1.dependents],
              ["Place of Birth", a1.place_of_birth],
              ["Nationality", a1.nationality],
              ["Email", a1.email],
              ["Mobile", phone(a1.ext_mob_contact, a1.mob_contact)],
              ["Home Phone", phone(a1.ext_home_contact, a1.home_contact)],
              ["Address", [a1.home_address, a1.address_line, a1.address_line_two].filter((p) => !isBlank(p)).join(", ")],
              ["City", a1.city_name],
              ["State", a1.state_name],
              ["Zip Code", a1.zip_code],
              ["Country", a1.country_name],
            ],
          },
        ]);

        const idGroup = (title, doc, withDates = true) => {
          if (!doc || (isBlank(doc.number) && isBlank(doc.isuue_country))) return null;
          const rows = [["Number", doc.number]];
          if (withDates) {
            rows.push(["Issue Date", day(doc.issue_on)], ["Expiry Date", day(doc.expire_on)]);
          }
          rows.push(["Country of Issuance", doc.isuue_country]);
          return { title, rows };
        };
        add(
          "Identification Details",
          a1,
          [
            idGroup("National Identification", a1.nat_identity),
            idGroup("Driver’s Permit", a1.driver),
            idGroup("Passport", a1.passport),
            idGroup("Birth Certificate", a1.birth_cert, false),
          ].filter(Boolean),
        );
      }

      const b2 = app.section_b2;
      add("Employment Details", b2, [
        {
          rows: b2
            ? [
                ["Employment Status", pretty(b2.emp_status)],
                ["Pay Frequency", pretty(b2.pay_freq)],
                ["Time in Employment", b2.emp_time],
                ["Average Monthly Income", b2.avg_mnth_income],
                ["Occupation", b2.occupation],
                ["Employer", b2.employer],
                ["Employer Address", b2.emp_address],
                ["Work Phone", phone(b2.ext_work_contact, b2.work_contact)],
                ["Extension", b2.extra_ext_work],
                ["School", b2.school],
              ]
            : [],
        },
      ]);

      const c3 = app.section_c3;
      add("Account Activity", c3, [
        {
          rows: c3
            ? [
                ["How the Account Is Funded", c3.fund_source],
                ["Member of Another Credit Union", yesNo(c3.mem_in_others)],
                ["Credit Union Details", c3.mem_in_others_desc],
                ["Other Details", c3.others_details],
                ["Source of Income", c3.socuce_of_income],
                ["Area of Details", c3.area_of_details],
                ["Number of Transactions", c3.nos_trx],
                ["Volume of Transactions", money(c3.vol_trx)],
              ]
            : [],
        },
      ]);

      const d4 = app.section_d4;
      if (this.isMinor) {
        add("Guardian Details", d4, [
          {
            title: "Parent / Guardian",
            rows: d4 && d4.incare_g
              ? [["Name", d4.incare_g.name], ["Identification", d4.incare_g.identification]]
              : [],
          },
          {
            title: "Trustee",
            rows: d4 && d4.incare_t
              ? [["Name", d4.incare_t.name], ["Identification", d4.incare_t.identification]]
              : [],
          },
          { rows: d4 ? [["Advisor", d4.advisor]] : [] },
        ]);
      }

      const e5 = app.section_e5;
      add("PEP Declaration", e5, [
        {
          rows: e5
            ? [
                ["Entrusted with a prominent public function", yesNo(e5.prom_entrust)],
                ["Immediate family member of such a person", yesNo(e5.immd_famly)],
                ["Close personal associate of such a person", yesNo(e5.close_prsonl)],
              ]
            : [],
        },
      ]);

      const f6 = app.section_f6;
      add("FATCA", f6, [
        {
          rows: f6
            ? [
                ["Citizen of another country", yesNo(f6.other_citizen)],
                ["US citizen", yesNo(f6.us_citizen)],
                ["SSN / TIN", f6.ssn_no],
                ["US address", yesNo(f6.us_address)],
                ["Standing instructions to a US account", yesNo(f6.givin_instruct)],
              ]
            : [],
        },
      ]);

      const g7 = app.section_g7;
      add("Account & Fees", g7, [
        {
          rows: g7
            ? [
                ["Shares Account", g7.shares_acc],
                ["Shares Amount", money(g7.shares_amt)],
                ["Special Shares Account", g7.spshares_ac],
                ["Special Shares Amount", money(g7.spshares_amt)],
                ["Deposit Account", g7.deposit_ac],
                ["Deposit Amount", money(g7.deposit_amt)],
                ["Registration Fee", money(g7.reg_fee)],
                ["Total", money(g7.total)],
              ]
            : [],
        },
      ]);

      const h8 = app.section_h8;
      if (h8) {
        const nomineeGroup = (title, n) =>
          n
            ? {
                title,
                rows: [
                  ["Name", n.name],
                  ["Relationship", String(n.relation).toLowerCase().startsWith("other") && !isBlank(n.other_relative) ? n.other_relative : n.relation],
                  ["Date of Birth", day(n.applicant_dob)],
                  ["Email", n.email_address],
                  ["Phone", phone(n.ext_contact, n.contact)],
                  ["Address", n.address],
                ],
              }
            : null;
        add(
          "Nominees",
          h8,
          [
            nomineeGroup("Nominee 1", h8.nominee_1),
            nomineeGroup("Nominee 2", h8.nominee_2),
            nomineeGroup(
              h8.nominee_minor && h8.nominee_minor.caretaker && h8.nominee_minor.caretaker.type
                ? `Nominee (${pretty(h8.nominee_minor.caretaker.type)})`
                : "Nominee (Minor)",
              h8.nominee_minor,
            ),
          ].filter(Boolean),
        );
      }

      const i9 = app.section_i9;
      add("Members Declaration", i9, [
        {
          rows: i9
            ? [
                ["Declaration accepted", yesNo(i9.decleration)],
                ["Declared On", i9.declared_at ? formatDate(i9.declared_at) : ""],
              ]
            : [],
        },
      ]);

      // each card shows "—" for an empty value and a note when it has nothing at all
      return list.map((section) => ({
        ...section,
        groups: section.groups.map((group) => ({
          ...group,
          rows: group.rows.map(([label, value]) => ({
            label,
            value: isBlank(value) ? "" : String(value),
          })),
        })),
      }));
    },
    // uploaded documents, from the same endpoint the staff review page uses
    documents() {
      const d = this.docs;
      if (!d) return [];
      const single = [
        ["National Identification", d.url],
        ["Driver’s Permit", d.url_two],
        ["Passport", d.url_three],
        ["Utility Bill", d.url_four],
        ["Birth Certificate", d.url_five],
        ["Applicant’s Signature", d.url_six],
        ["Parent / Guardian’s Signature", d.url_seven],
        ["Trustee’s Signature", d.url_eight],
      ]
        .filter(([, url]) => !!url)
        .map(([label, url]) => ({ label, url }));
      const slips = (Array.isArray(d.url_nine) ? d.url_nine : [])
        .filter(Boolean)
        .map((url, i) => ({ label: `Pay Slip / Job Letter ${i + 1}`, url }));
      return [...single, ...slips];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.loadError = false;
      axios
        .get(`/applications/single_application_details/${this.appId}`)
        .then((response) => {
          const data = response.data && response.data.app_data;
          if (!data) throw new Error("No application data");
          this.app = data;
          this.loadDocuments();
        })
        .catch((error) => {
          console.error("Failed to load the application:", error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadDocuments() {
      axios
        .get(`/applications/issue_image/${this.appId}/`)
        .then((response) => {
          this.docs = response.data || null;
        })
        .catch(() => {
          this.docs = null; // the card is simply left out
        });
    },
  },
};
</script>

<template>
  <ApplicantLayout>
    <section class="va">
      <header class="ecu-page-head">
        <div>
          <h1>My Application</h1>
          <p class="ecu-page-head__sub">Everything you submitted, on one page.</p>
        </div>
        <router-link to="/view_log" class="ecu-btn-outline">
          <AppIcon name="chevron-left" :size="14" /> Application Status
        </router-link>
      </header>

      <div v-if="loading" class="ecu-card">
        <div class="ecu-state"><h3>Loading your application…</h3></div>
      </div>

      <div v-else-if="loadError" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon ecu-state__icon--danger"><AppIcon name="alert" :size="26" /></span>
          <h3>We couldn't load your application</h3>
          <p>Please check your connection and try again.</p>
          <button type="button" class="ecu-btn-outline" @click="load">
            <AppIcon name="refresh" :size="14" /> Try again
          </button>
        </div>
      </div>

      <template v-else>
        <div class="ecu-card va__summary">
          <div v-for="item in summary" :key="item.label" class="va__summary-item">
            <small>{{ item.label }}</small>
            <strong>{{ item.value }}</strong>
          </div>
          <div class="va__summary-item">
            <small>Status</small>
            <StatusChip v-if="app.status" :status="app.status" />
            <strong v-else>N/A</strong>
          </div>
        </div>

        <div class="va__grid">
          <article v-for="section in sections" :key="section.title" class="ecu-card va__card">
            <h2 class="va__title">{{ section.title }}</h2>
            <p v-if="!section.groups.some((g) => g.rows.some((r) => r.value))" class="va__empty">
              Nothing was provided for this section.
            </p>
            <template v-else>
              <div v-for="(group, gi) in section.groups" :key="gi" class="va__group">
                <h3 v-if="group.title" class="va__subtitle">{{ group.title }}</h3>
                <dl class="va__list">
                  <div v-for="row in group.rows" :key="row.label" class="va__row">
                    <dt>{{ row.label }}</dt>
                    <dd>{{ row.value || "—" }}</dd>
                  </div>
                </dl>
              </div>
            </template>
          </article>

          <article v-if="documents.length" class="ecu-card va__card">
            <h2 class="va__title">Uploaded Documents</h2>
            <ul class="va__docs">
              <li v-for="doc in documents" :key="doc.label + doc.url">
                <span>{{ doc.label }}</span>
                <a :href="doc.url" target="_blank" rel="noopener noreferrer">View</a>
              </li>
            </ul>
          </article>
        </div>
      </template>
    </section>
  </ApplicantLayout>
</template>

<style scoped>
.va__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  padding: 16px 20px;
  margin-bottom: 16px;
}
.va__summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
}
.va__summary-item small {
  font-size: var(--ecu-fs-small);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ecu-muted);
}
.va__summary-item strong {
  font-size: var(--ecu-fs-body);
  color: var(--ecu-navy);
}

/* two sections per row; one per row on narrow screens */
.va__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}
.va__card {
  padding: 18px 20px;
}
.va__title {
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--ecu-border);
  font-size: var(--ecu-fs-h3);
  font-weight: 700;
  color: var(--ecu-navy);
}
.va__subtitle {
  margin: 12px 0 6px;
  font-size: var(--ecu-fs-small);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ecu-orange-dark);
}
.va__group:first-of-type .va__subtitle {
  margin-top: 0;
}
.va__list {
  margin: 0;
}
.va__row {
  display: grid;
  grid-template-columns: minmax(110px, 40%) minmax(0, 1fr);
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #f4f5fa;
}
.va__row:last-child {
  border-bottom: 0;
}
.va__row dt {
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-normal);
  color: var(--ecu-muted);
}
.va__row dd {
  margin: 0;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-normal);
  color: var(--ecu-ink);
  overflow-wrap: anywhere;
}
.va__empty {
  margin: 0;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-muted);
}
.va__docs {
  margin: 0;
  padding: 0;
  list-style: none;
}
.va__docs li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f4f5fa;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-ink);
}
.va__docs li:last-child {
  border-bottom: 0;
}
.va__docs a {
  font-weight: 700;
  color: var(--ecu-orange-dark);
  text-decoration: none;
}

@media (max-width: 991px) {
  .va__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 575px) {
  .va__row {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }
}
</style>